import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertCircle, BookOpen, Check, CheckCircle2, ChevronUp, Download, Loader2,
  Pencil, Plus, Save, Star, Trash2, Upload,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  deleteSharedConfig,
  getSharedConfig,
  listSharedConfigs,
  saveSharedConfig,
  updateSharedConfig,
} from '../../services/api/sharedConfig.api';
import {
  getStoredActiveSharedConfigId,
  setStoredActiveSharedConfigId,
} from '../../services/activeSharedConfigStorage';
import { supabase } from '../../lib/supabase';
import { upsertUserDashboardState } from '../../services/userDashboardState';

const formatDate = (isoString) => {
  if (!isoString) return '';
  try {
    return new Date(isoString).toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return isoString;
  }
};

const InlineForm = ({ initialName = '', initialDescription = '', submitLabel, onSubmit, onCancel, loading }) => {
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSubmit(name.trim(), description.trim());
  };

  return (
    <form onSubmit={handleSubmit} className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Name *</label>
        <input
          autoFocus
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="z. B. Produktions-Dashboard"
          maxLength={80}
          className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Beschreibung</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Kurze Beschreibung (optional)"
          rows={2}
          maxLength={200}
          className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
        />
      </div>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 transition-colors"
        >
          Abbrechen
        </button>
        <button
          type="submit"
          disabled={!name.trim() || loading}
          className="flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {loading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          {submitLabel}
        </button>
      </div>
    </form>
  );
};

const ConfigManagerModal = ({
  open, onClose, currentUserId,
  onBuildPayload, onApplyConfig, onPersistConfig,
  onSave, onExport, onImport, isSaving, saveError,
}) => {
  const [configs, setConfigs] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState(null);

  const [editingId, setEditingId] = useState(null);
  const [savingNew, setSavingNew] = useState(false);
  const [actionLoading, setActionLoading] = useState({});
  const [saveNewLoading, setSaveNewLoading] = useState(false);
  const [defaultedId, setDefaultedId] = useState(null);
  const [activeConfigId, setActiveConfigId] = useState(null);

  // Save button feedback
  const [justSaved, setJustSaved] = useState(false);
  const savedTimerRef = useRef(null);
  useEffect(() => () => clearTimeout(savedTimerRef.current), []);

  const importInputRef = useRef(null);

  const isNoAuth = !currentUserId || currentUserId === 'default';
  const canManage = (cfg) => isNoAuth || cfg.created_by === currentUserId;

  const fetchConfigs = useCallback(async () => {
    setListLoading(true);
    setListError(null);
    try {
      setConfigs(await listSharedConfigs());
    } catch (e) {
      setListError('Konfigurationen konnten nicht geladen werden.');
      console.error('Failed to list shared configs:', e);
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchConfigs();
      setActiveConfigId(getStoredActiveSharedConfigId(currentUserId));
    }
  }, [open, fetchConfigs, currentUserId]);

  // Marks which shared config is "active" (shown with the green "Aktiv" badge).
  // Per-browser via localStorage, plus a best-effort sync to Supabase for
  // logged-in users so the badge follows them across devices — mirroring how
  // the config's own content already syncs via dashboard_config_saved_at (see
  // useDashboardConfig.js). Only the id/name pointer lives in Supabase; the
  // config content itself is unaffected by this call.
  const markActive = (id, name = null) => {
    setStoredActiveSharedConfigId(currentUserId, id);
    setActiveConfigId(id);
    if (isNoAuth) return;
    supabase.auth
      .getSession()
      .then(({ data: { session } }) => {
        if (!session?.user) return;
        return upsertUserDashboardState({
          active_shared_config_id: id,
          active_shared_config_name: id ? name : null,
        });
      })
      .catch(() => {});
  };

  // ── Personal config actions ────────────────────────────────────────────────

  const handleSaveClick = async () => {
    clearTimeout(savedTimerRef.current);
    setJustSaved(false);
    const ok = await onSave?.();
    if (ok) {
      setJustSaved(true);
      savedTimerRef.current = setTimeout(() => setJustSaved(false), 2000);
    }
  };

  const handleImportFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await onImport?.(file);
    e.target.value = '';
    onClose();
  };

  // ── Shared config actions ──────────────────────────────────────────────────

  const handleLoad = async (cfg) => {
    setActionLoading((p) => ({ ...p, [cfg.id]: 'load' }));
    try {
      const entry = await getSharedConfig(cfg.id);
      onApplyConfig(entry.config);
      onPersistConfig?.(entry.config);
      markActive(cfg.id, cfg.name);
      onClose();
    } catch (e) {
      console.error('Failed to load shared config:', e);
    } finally {
      setActionLoading((p) => ({ ...p, [cfg.id]: null }));
    }
  };

  const handleSetDefault = async (cfg) => {
    setActionLoading((p) => ({ ...p, [cfg.id]: 'default' }));
    try {
      const entry = await getSharedConfig(cfg.id);
      await onPersistConfig?.(entry.config);
      setDefaultedId(cfg.id);
      setTimeout(() => setDefaultedId(null), 2500);
    } catch (e) {
      console.error('Failed to set default config:', e);
    } finally {
      setActionLoading((p) => ({ ...p, [cfg.id]: null }));
    }
  };

  const handleDelete = async (cfg) => {
    if (!window.confirm(`"${cfg.name}" wirklich löschen?`)) return;
    setActionLoading((p) => ({ ...p, [cfg.id]: 'delete' }));
    try {
      await deleteSharedConfig(cfg.id);
      setConfigs((prev) => prev.filter((c) => c.id !== cfg.id));
      if (activeConfigId === cfg.id) markActive(null);
    } catch (e) {
      console.error('Failed to delete shared config:', e);
    } finally {
      setActionLoading((p) => ({ ...p, [cfg.id]: null }));
    }
  };

  const handleSaveNew = async (name, description) => {
    setSaveNewLoading(true);
    try {
      const config = onBuildPayload();
      const created = await saveSharedConfig({ name, description, config });
      setSavingNew(false);
      await fetchConfigs();
      if (created?.id) markActive(created.id, name);
    } catch (e) {
      console.error('Failed to save shared config:', e);
    } finally {
      setSaveNewLoading(false);
    }
  };

  const handleUpdate = async (cfg, name, description) => {
    setActionLoading((p) => ({ ...p, [cfg.id]: 'update' }));
    try {
      const config = onBuildPayload();
      await updateSharedConfig(cfg.id, { name, description, config });
      setEditingId(null);
      await fetchConfigs();
      markActive(cfg.id, name);
    } catch (e) {
      console.error('Failed to update shared config:', e);
    } finally {
      setActionLoading((p) => ({ ...p, [cfg.id]: null }));
    }
  };

  const showSaveError = !isSaving && !justSaved && !!saveError;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[88vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <BookOpen className="w-4 h-4 text-blue-600" />
            Konfigurationsmanager
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 pr-1">

          {/* ── Personal actions ── */}
          <div className="flex items-center gap-2 p-3 bg-gray-50 border border-gray-200 rounded-lg">
            {/* Save */}
            <button
              onClick={handleSaveClick}
              disabled={isSaving}
              title={showSaveError ? `Speichern fehlgeschlagen: ${saveError}` : 'Aktuelles Layout speichern'}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                showSaveError
                  ? 'text-red-600 bg-white border-red-300 hover:bg-red-50'
                  : justSaved
                    ? 'text-green-700 bg-green-50 border-green-300'
                    : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'
              }`}
            >
              {isSaving
                ? <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                : justSaved
                  ? <Check className="w-4 h-4 text-green-600" />
                  : showSaveError
                    ? <AlertCircle className="w-4 h-4 text-red-500" />
                    : <Save className="w-4 h-4 text-gray-400" />}
              {isSaving ? 'Speichern…' : justSaved ? 'Gespeichert' : showSaveError ? 'Fehler' : 'Speichern'}
            </button>

            <div className="w-px h-5 bg-gray-200" />

            {/* Import file */}
            <button
              onClick={() => importInputRef.current?.click()}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              title="Konfigurationsdatei importieren"
            >
              <Upload className="w-4 h-4 text-gray-400" />
              Import file
            </button>

            {/* Export file */}
            <button
              onClick={onExport}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              title="Konfiguration als Datei exportieren"
            >
              <Download className="w-4 h-4 text-gray-400" />
              Export file
            </button>

            <input
              ref={importInputRef}
              type="file"
              accept=".json,application/json"
              className="hidden"
              onChange={handleImportFileChange}
            />
          </div>

          {/* ── Save as shared config ── */}
          <div>
            {!savingNew ? (
              <button
                onClick={() => { setSavingNew(true); setEditingId(null); }}
                className="flex items-center gap-2 w-full px-4 py-2.5 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Aktuelles Layout als gemeinsame Konfiguration speichern
              </button>
            ) : (
              <InlineForm
                submitLabel="Als gemeinsame Konfiguration speichern"
                onSubmit={handleSaveNew}
                onCancel={() => setSavingNew(false)}
                loading={saveNewLoading}
              />
            )}
          </div>

          {/* ── Shared config list ── */}
          <div className="border-t border-gray-100 pt-3">
            {listLoading && (
              <div className="flex items-center justify-center py-8 text-gray-400 gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">Laden…</span>
              </div>
            )}

            {listError && (
              <p className="text-sm text-red-500 text-center py-4">{listError}</p>
            )}

            {!listLoading && !listError && configs.length === 0 && (
              <p className="text-sm text-gray-400 text-center py-8">
                Noch keine gemeinsamen Konfigurationen vorhanden.<br />
                Speichern Sie Ihr aktuelles Layout, um zu beginnen.
              </p>
            )}

            {!listLoading && !listError && configs.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                  Gemeinsame Konfigurationen ({configs.length})
                </p>
                {configs.map((cfg) => {
                  const rowLoading = actionLoading[cfg.id];
                  const isDefaulted = defaultedId === cfg.id;
                  const isEditing = editingId === cfg.id;
                  const isActive = activeConfigId === cfg.id;
                  const manage = canManage(cfg);

                  return (
                    <div
                      key={cfg.id}
                      className={`border rounded-lg overflow-hidden ${
                        isActive ? 'border-green-300 ring-1 ring-green-200' : 'border-gray-200'
                      }`}
                    >
                      <div className="flex items-start gap-3 px-4 py-3 bg-white">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-gray-800 truncate">{cfg.name}</p>
                            {isActive && (
                              <span className="flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium text-green-700 bg-green-50 border border-green-200 rounded shrink-0">
                                <CheckCircle2 className="w-3 h-3" />
                                Aktiv
                              </span>
                            )}
                          </div>
                          {cfg.description && (
                            <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{cfg.description}</p>
                          )}
                          <p className="text-xs text-gray-400 mt-1">
                            {cfg.created_by_label !== 'default' ? cfg.created_by_label : 'Lokal'} · {formatDate(cfg.updated_at)}
                          </p>
                        </div>

                        <div className="flex items-center gap-1.5 shrink-0">
                          {/* Load */}
                          <button
                            onClick={() => handleLoad(cfg)}
                            disabled={!!rowLoading}
                            title="Konfiguration jetzt laden"
                            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-40 transition-colors"
                          >
                            {rowLoading === 'load' && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                            Laden
                          </button>

                          {/* Set as default */}
                          <button
                            onClick={() => handleSetDefault(cfg)}
                            disabled={!!rowLoading}
                            title="Als Standard festlegen (beim nächsten Start automatisch geladen)"
                            className={`flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md border transition-colors disabled:opacity-40 ${
                              isDefaulted
                                ? 'text-amber-700 bg-amber-50 border-amber-300'
                                : 'text-gray-600 bg-white border-gray-300 hover:border-amber-400 hover:text-amber-600'
                            }`}
                          >
                            {rowLoading === 'default'
                              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              : <Star className={`w-3.5 h-3.5 ${isDefaulted ? 'fill-amber-500 text-amber-500' : ''}`} />}
                            {isDefaulted ? 'Standard gesetzt' : 'Standard'}
                          </button>

                          {/* Edit (owners only) */}
                          {manage && (
                            <button
                              onClick={() => setEditingId(isEditing ? null : cfg.id)}
                              disabled={!!rowLoading}
                              title="Konfiguration bearbeiten"
                              className="p-1.5 text-gray-400 hover:text-blue-600 disabled:opacity-40 transition-colors rounded"
                            >
                              {isEditing ? <ChevronUp className="w-4 h-4" /> : <Pencil className="w-4 h-4" />}
                            </button>
                          )}

                          {/* Delete (owners only) */}
                          {manage && (
                            <button
                              onClick={() => handleDelete(cfg)}
                              disabled={!!rowLoading}
                              title="Konfiguration löschen"
                              className="p-1.5 text-gray-400 hover:text-red-600 disabled:opacity-40 transition-colors rounded"
                            >
                              {rowLoading === 'delete'
                                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                : <Trash2 className="w-4 h-4" />}
                            </button>
                          )}
                        </div>
                      </div>

                      {isEditing && (
                        <div className="px-4 pb-3 border-t border-gray-100 bg-gray-50">
                          <div className="pt-3">
                            <InlineForm
                              initialName={cfg.name}
                              initialDescription={cfg.description}
                              submitLabel="Aktualisieren"
                              onSubmit={(name, description) => handleUpdate(cfg, name, description)}
                              onCancel={() => setEditingId(null)}
                              loading={rowLoading === 'update'}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ConfigManagerModal;
